import { Link } from 'react-router-dom'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Cabeçalho padrão das páginas internas (rotas de detalhe/ID).
 *
 * Estrutura: botão voltar + trilha (breadcrumb) + título, com slots
 * opcionais para badge de status, informações resumidas e ações.
 *
 * @param {string}  [backTo]    rota do botão voltar (ex.: '/contrato')
 * @param {string}  [backLabel] rótulo da seção de origem (ex.: 'Contratos')
 * @param {string}  title       título da página
 * @param {string}  [crumb]     texto final da trilha (default: title)
 * @param {node}    [subtitle]  linha de apoio abaixo do título
 * @param {node}    [badge]     badge exibido ao lado do título
 * @param {Array}   [meta]      [{ label, value }] exibidos como resumo
 * @param {node}    [actions]   botões à direita
 */
export function PageHeader({
  backTo,
  backLabel,
  title,
  crumb,
  subtitle,
  badge,
  meta,
  actions,
  className,
  children,
}) {
  const items = (meta || []).filter((m) => m && m.value != null && m.value !== '')

  return (
    <header
      className={cn(
        'sticky top-0 z-20 shrink-0 border-b border-border',
        'bg-card/85 backdrop-blur-sm supports-[backdrop-filter]:bg-card/70',
        'px-6 py-4',
        className
      )}
    >
      <div className="flex items-start gap-3">
        {backTo && (
          <Link
            to={backTo}
            aria-label={backLabel ? `Voltar para ${backLabel}` : 'Voltar'}
            title={backLabel ? `Voltar para ${backLabel}` : 'Voltar'}
            className={cn(
              'mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md',
              'border border-border bg-background text-muted-foreground',
              'transition-colors hover:bg-accent/60 hover:text-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            )}
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
        )}

        <div className="min-w-0 flex-1">
          {backLabel && (
            <nav aria-label="Trilha de navegação" className="flex items-center gap-1 text-xs text-muted-foreground">
              {backTo ? (
                <Link to={backTo} className="transition-colors hover:text-foreground">
                  {backLabel}
                </Link>
              ) : (
                <span>{backLabel}</span>
              )}
              <ChevronRight className="h-3 w-3 opacity-60" />
              <span className="truncate font-medium text-foreground/70">{crumb || title}</span>
            </nav>
          )}

          <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <h1 className="truncate text-2xl font-semibold tracking-tight">{title}</h1>
            {badge}
          </div>

          {subtitle && <p className="mt-1 truncate text-sm text-muted-foreground">{subtitle}</p>}

          {items.length > 0 && (
            <dl className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted-foreground">
              {items.map((m) => (
                <div key={m.label} className="flex items-center gap-1.5">
                  <dt className="uppercase tracking-wide">{m.label}</dt>
                  <dd className="font-medium text-foreground">{m.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>

      {children && <div className="mt-4">{children}</div>}
    </header>
  )
}

export default PageHeader
